import type { Company } from "@domain/CRM/company";
import type { Job } from "@domain/CRM/job";
import type { IJobs } from "@ports/jobs/IJobs";

interface JobsListUseCasesDeps {
	jobsApi: IJobs;
}

/**
 * Read path for the jobs list (JobsDashboardTable). Thin pass-through to the
 * `IJobs` port; no business logic. Exists so VMs depend on a use case rather
 * than `API.Data.Jobs` directly — swap-in test/CLI implementations just supply
 * the port.
 */
export interface JobsListUseCases {
	loadByCompany(company: Company, branchNames?: string[], onBatch?: (jobs: Job[]) => void): Promise<Job[]>;
	loadUnscheduled(): Promise<Job[]>;
	ensureAttachments(jobs: Job[]): Promise<void>;
}

/**
 * With `onBatch`, jobs stream in batches for progressive rendering (the
 * callback fires per batch; the promise still resolves with the full list).
 * Without it, a single request returns everything at once.
 */
function loadByCompany(
	deps: JobsListUseCasesDeps,
	company: Company,
	branchNames?: string[],
	onBatch?: (jobs: Job[]) => void,
): Promise<Job[]> {
	if (onBatch) return deps.jobsApi.getByCompanyStream(company, branchNames, onBatch);
	return deps.jobsApi.getByCompany(company, branchNames);
}

function loadUnscheduled(deps: JobsListUseCasesDeps): Promise<Job[]> {
	return deps.jobsApi.getUnscheduled();
}

/**
 * Populate `job.attachments` on jobs that came from a lite list payload
 * (where attachments are omitted and arrive as null). Mutates the given Job
 * instances in place; jobs that already have attachments are skipped, so
 * repeat calls are cheap no-ops.
 */
async function ensureAttachments(deps: JobsListUseCasesDeps, jobs: Job[]): Promise<void> {
	const missing = jobs.filter((job) => job.id && job.attachments == null);
	if (missing.length === 0) return;
	const byId = await deps.jobsApi.getAttachmentsByIds(missing.map((job) => job.id!));
	for (const job of missing) {
		job.attachments = byId[job.id!] ?? {};
	}
}

export function makeJobsListUseCases(deps: JobsListUseCasesDeps): JobsListUseCases {
	return {
		loadByCompany: (company, branchNames, onBatch) => loadByCompany(deps, company, branchNames, onBatch),
		loadUnscheduled: () => loadUnscheduled(deps),
		ensureAttachments: (jobs) => ensureAttachments(deps, jobs),
	};
}
